import React, { useState, useEffect } from 'react';

const Icon = ({ d, size = 16, strokeWidth = 2, color = 'currentColor', className = '' }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    operator: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M9 11a4 4 0 100-8 4 4 0 000 8z',
    plan: 'M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z',
    transaction: 'M12 1v22 M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6',
    calendar: 'M19 4H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z M16 2v4 M8 2v4 M3 10h18',
    check: 'M20 6L9 17l-5-5'
};

const defaultForm = {
    operatorId: '',
    planId: '',
    amount: '',
    transactionType: '',
    startDate: new Date().toISOString().split('T')[0],
    nextBillingDate: '',
};

export default function CreateSubscription() {
    const [form, setForm] = useState(defaultForm);
    const [operators, setOperators] = useState([]);
    const [plans, setPlans] = useState([]);
    const [existingSubscription, setExistingSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchingSub, setFetchingSub] = useState(false);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [opsRes, plansRes] = await Promise.all([
                fetch('/api/operators'),
                fetch('/api/price-plans')
            ]);
            const ops = await opsRes.json();
            const pls = await plansRes.json();
            setOperators(Array.isArray(ops) ? ops : []);
            setPlans(Array.isArray(pls) ? pls : []);
        } catch (err) {
            console.error('Error fetching data:', err);
            showToast('Failed to load operators or plans', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (form.operatorId) {
            fetchExistingSubscription(form.operatorId);
        } else {
            setExistingSubscription(null);
        }
    }, [form.operatorId]);

    const fetchExistingSubscription = async (opId) => {
        setFetchingSub(true);
        try {
            const res = await fetch(`/api/subscriptions/operator/${opId}`);
            if (res.ok) {
                const data = await res.json();
                setExistingSubscription(data);
            } else {
                setExistingSubscription(null);
            }
        } catch (err) {
            console.error('Error fetching existing subscription:', err);
            setExistingSubscription(null);
        } finally {
            setFetchingSub(false);
        }
    };

    useEffect(() => {
        if (form.planId && form.startDate) {
            calculateNextBilling(form.planId, form.startDate);
        }
    }, [form.planId, form.startDate, plans]);

    useEffect(() => {
        if (form.planId) {
            const plan = plans.find(p => p._id === form.planId);
            if (plan) {
                setForm(f => ({ ...f, amount: plan.price }));
            }
        } else {
            setForm(f => ({ ...f, amount: '' }));
        }
    }, [form.planId, plans]);

    const calculateNextBilling = (planId, startDate) => {
        const plan = plans.find(p => p._id === planId);
        if (!plan || !plan.pricePeriod) return;

        // Parse YYYY-MM-DD to local date
        const [y, m, d] = startDate.split('-').map(Number);
        const date = new Date(y, m - 1, d);

        const periodStr = plan.pricePeriod.toLowerCase();
        const matches = periodStr.match(/\d+/);
        const val = matches ? parseInt(matches[0]) : null;

        if (periodStr.includes('month') || periodStr.includes('mon')) {
            date.setMonth(date.getMonth() + (val || 1));
        } else if (periodStr.includes('quarter')) {
            date.setMonth(date.getMonth() + ((val || 1) * 3));
        } else if (periodStr.includes('half')) {
            date.setMonth(date.getMonth() + ((val || 1) * 6));
        } else if (periodStr.includes('year') || periodStr.includes('yr') || periodStr.includes('annua')) {
            date.setFullYear(date.getFullYear() + (val || 1));
        } else if (periodStr.includes('week') || periodStr.includes('wk')) {
            date.setDate(date.getDate() + ((val || 1) * 7));
        } else if (val !== null) {
            // If it's just a number (e.g. "60", "365") or "60 days"
            date.setDate(date.getDate() + val);
        } else {
            // Default 30 days if unrecognized
            date.setDate(date.getDate() + 30);
        }

        // Format back to YYYY-MM-DD using local components to avoid UTC shift
        const resY = date.getFullYear();
        const resM = String(date.getMonth() + 1).padStart(2, '0');
        const resD = String(date.getDate()).padStart(2, '0');
        const formattedDate = `${resY}-${resM}-${resD}`;

        setForm(f => ({ ...f, nextBillingDate: formattedDate }));
    };

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
    };

    const handleSave = async () => {
        if (!form.operatorId || !form.planId || !form.transactionType) {
            showToast('All fields are required', 'error');
            return;
        }
        setSaving(true);
        try {
            const res = await fetch('/api/subscriptions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (res.ok) {
                showToast('Subscription created successfully!', 'success');
                setForm(defaultForm);
            } else {
                showToast(data.message || 'Failed to save', 'error');
            }
        } catch {
            showToast('Server error', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="pp-page">Loading...</div>;

    return (
        <div className="pp-page">
            {toast && (
                <div className={`pp-toast ${toast.type}`}>
                    {toast.msg}
                </div>
            )}

            <div className="pp-banner">
                <div className="pp-banner-left">
                    <div className="pp-banner-icon">
                        <Icon d={icons.plan} size={26} color="#00B2A9" />
                    </div>
                    <div>
                        <h1 className="pp-banner-title">Create Subscription</h1>
                        <p className="pp-banner-sub">Link an operator to a price plan</p>
                    </div>
                </div>
                <button className="pp-submit-btn" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Subscription'}
                </button>
            </div>

            <div className="pp-layout">
                <div className="pp-main-col">
                    <div className="pp-card">
                        <div className="pp-card-label">
                            <Icon d={icons.operator} size={14} color="#00B2A9" style={{ marginRight: 6 }} />
                            Select Operator
                        </div>
                        <select
                            className="pp-input pp-input-lg"
                            name="operatorId"
                            value={form.operatorId}
                            onChange={handleChange}
                        >
                            <option value="">-- Choose Operator --</option>
                            {operators.map(op => (
                                <option key={op._id} value={op._id}>{op.companyName}</option>
                            ))}
                        </select>
                    </div>

                    {fetchingSub && (
                        <div className="pp-card" style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                            Checking for existing subscription...
                        </div>
                    )}

                    {existingSubscription && !fetchingSub && (
                        <div className="pp-card" style={{ border: '2px solid #00B2A922', background: 'rgba(99, 102, 241, 0.03)' }}>
                            <div className="pp-card-label" style={{ color: '#00B2A9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Icon d={icons.check} size={14} color="#00B2A9" style={{ marginRight: 6 }} />
                                    Active Subscription Found
                                </div>
                                <span style={{
                                    fontSize: '11.14px',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    background: existingSubscription.status === 'Active' ? '#dcfce7' : '#fee2e2',
                                    color: existingSubscription.status === 'Active' ? '#166534' : '#991b1b',
                                    fontWeight: '600'
                                }}>
                                    {existingSubscription.status}
                                </span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                                <div>
                                    <div style={{ fontSize: '12.25px', color: '#94a3b8', marginBottom: '2px' }}>Plan</div>
                                    <div style={{ fontSize: '15.60px', fontWeight: '600', color: '#1e293b' }}>
                                        {existingSubscription.planId?.name || 'Unknown Plan'}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '12.25px', color: '#94a3b8', marginBottom: '2px' }}>Pricing</div>
                                    <div style={{ fontSize: '15.60px', fontWeight: '600', color: '#1e293b' }}>
                                        ${existingSubscription.planId?.price || 0} / {existingSubscription.planId?.pricePeriod || 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '12.25px', color: '#94a3b8', marginBottom: '2px' }}>Started On</div>
                                    <div style={{ fontSize: '15.60px', color: '#334155' }}>
                                        {new Date(existingSubscription.startDate).toLocaleDateString()}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '12.25px', color: '#94a3b8', marginBottom: '2px' }}>Next Billing</div>
                                    <div style={{ fontSize: '15.60px', color: '#334155' }}>
                                        {new Date(existingSubscription.nextBillingDate).toLocaleDateString()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="pp-card">
                        <div className="pp-card-label">
                            <Icon d={icons.plan} size={14} color="#14b8a6" style={{ marginRight: 6 }} />
                            Select Price Plan
                        </div>
                        <select
                            className="pp-input pp-input-lg"
                            name="planId"
                            value={form.planId}
                            onChange={handleChange}
                        >
                            <option value="">-- Choose Plan --</option>
                            {plans.map(p => (
                                <option key={p._id} value={p._id}>{p.name} (${p.price} / {p.pricePeriod})</option>
                            ))}
                        </select>
                    </div>

                    {form.planId && (
                        <div className="pp-card" style={{
                            border: '1px solid #14b8a644',
                            background: 'rgba(20, 184, 166, 0.04)',
                            marginTop: '-10px',
                            marginBottom: '20px',
                            padding: '12px 16px'
                        }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                                <div>
                                    <div style={{ fontSize: '11.14px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '4px' }}>
                                        Plan Price
                                    </div>
                                    <div style={{ fontSize: '20.05px', fontWeight: '700', color: '#0d9488' }}>
                                        ${form.amount} <span style={{ fontSize: '13.36px', fontWeight: 'normal', color: '#52525b' }}>/ {plans.find(p => p._id === form.planId)?.pricePeriod}</span>
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '11.14px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '4px' }}>
                                        Expiration Date
                                    </div>
                                    <div style={{ fontSize: '16.71px', fontWeight: '600', color: '#334155', display: 'flex', alignItems: 'center' }}>
                                        <Icon d={icons.calendar} size={14} color="#14b8a6" style={{ marginRight: 8 }} />
                                        {form.nextBillingDate ? new Date(form.nextBillingDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Calculating...'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}


                    <div className="pp-pricing-row">
                        <div className="pp-card" style={{ flex: 1 }}>
                            <div className="pp-card-label">
                                <Icon d={icons.transaction} size={14} color="#f59e0b" style={{ marginRight: 6 }} />
                                Amount
                            </div>
                            <input
                                className="pp-input pp-input-lg"
                                type="number"
                                name="amount"
                                value={form.amount}
                                readOnly
                                style={{ background: '#f8fafc' }}
                                placeholder="Auto-calculated from plan"
                            />
                        </div>
                        <div className="pp-card" style={{ flex: 1, marginLeft: 20 }}>
                            <div className="pp-card-label">
                                <Icon d={icons.transaction} size={14} color="#00B2A9" style={{ marginRight: 6 }} />
                                Transaction Type
                            </div>
                            <input
                                className="pp-input pp-input-lg"
                                type="text"
                                name="transactionType"
                                value={form.transactionType}
                                onChange={handleChange}
                                placeholder="e.g. Credit Card, Bank Transfer"
                            />
                        </div>
                    </div>

                    <div className="pp-pricing-row">
                        <div className="pp-card" style={{ flex: 1 }}>
                            <div className="pp-card-label">
                                <Icon d={icons.calendar} size={14} color="#10b981" style={{ marginRight: 6 }} />
                                Start Date
                            </div>
                            <input
                                className="pp-input pp-input-lg"
                                type="date"
                                name="startDate"
                                value={form.startDate}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="pp-card" style={{ flex: 1, marginLeft: 20 }}>
                            <div className="pp-card-label">
                                <Icon d={icons.calendar} size={14} color="#ef4444" style={{ marginRight: 6 }} />
                                Next Billing Date
                            </div>
                            <input
                                className="pp-input pp-input-lg"
                                type="date"
                                name="nextBillingDate"
                                value={form.nextBillingDate}
                                readOnly
                                style={{ background: '#f8fafc' }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
